/**
 * Live-wire store — a store slot that owns the active `WireClient` handle
 * so UI components can dispatch intents without passing the handle through
 * prop chains.
 *
 * Populated by `bootstrapWireClient()` at app boot; read by the chat pane
 * (D9), moderation UI (D13), inspection intents (D12), and anything else
 * that emits intents. `send()` is a no-op when no client is live (happens
 * briefly between strict-mode mount cycles); the caller can check
 * `getState().ready` if a hard failure is preferred.
 */

import { create } from 'zustand';

import { phase0, debugLog } from '../log';
import { checkLimit, classifyIntent, useThrottle } from '../throttle';
import { useToasts } from '../toasts';
import type { ClientIntent, WireClient } from './index';

export interface LiveWireState {
  client: WireClient | null;
  ready: boolean;
  setClient: (client: WireClient | null) => void;
  /** Dispatch an intent through the rate-limit throttle. Returns `true`
   *  if the send reached the wire, `false` if it was rejected locally
   *  (advisory) and a toast was pushed. Callers that need a silent
   *  drop can pre-check via `checkLimit` themselves. */
  send: (intent: ClientIntent) => boolean;
}

export const useLiveWire = create<LiveWireState>((set, get) => ({
  client: null,
  ready: false,

  setClient: (client) => set(() => ({ client, ready: client !== null })),

  send: (intent) => {
    const c = get().client;
    if (!c) return false;

    // Advisory rate-limit check (D16) — tighter-wins across aggregate
    // WorldIntent + family per-tick + family per-minute.
    const family = classifyIntent(intent);
    const nowMs =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const snapshot = useThrottle.getState().snapshot();
    const check = checkLimit(snapshot, family, nowMs);
    if (!check.ok) {
      phase0.warn('rate_limited', {
        family,
        reason: check.reason ?? 'unknown',
        retry_after_ms: check.retryAfterMs,
      });
      debugLog('wire', 'warn', `rate_limited family=${family} reason=${check.reason ?? 'unknown'}`);
      if (family !== 'move') {
        useToasts
          .getState()
          .push('warning', check.reason ?? 'rate-limited — slow down');
      }
      return false;
    }

    try {
      c.send(intent);
      useThrottle.getState().recordSend(family, nowMs);
      phase0.debug('intent_send', { family, plane: intent.plane });
      debugLog('wire', 'debug', `intent_send family=${family} plane=${intent.plane}`);
      return true;
    } catch (err) {
      phase0.error('wire_send_failed', {
        family,
        reason: err instanceof Error ? err.message : String(err),
      });
      debugLog('wire', 'error', `wire_send_failed family=${family} reason=${err instanceof Error ? err.message : String(err)}`);
      return false;
    }
  },
}));
